import { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { PersistState } from 'redux-persist';
import { RootState } from 'typesafe-actions';
import { HydrationStateEnum } from 'types/redux';
import { isPersistedState } from 'types/guards/redux';

interface ReduxPersist {
  _persist?: PersistState | unknown;
}

type RootStateWithPersist = {
  [K in keyof RootState]: RootState[K] & ReduxPersist;
};

export const useIsReduxStatesHydrated = (
  statesToCheck: HydrationStateEnum[]
): {
  rehydrated: boolean;
  timedOut: boolean;
} => {
  const [hasRehydrationTimedOut, setHasRehydrationTimedOut] = useState(false);

  const rootState: RootStateWithPersist = useSelector(
    (state: RootState) => state
  );

  const states = useMemo(() => statesToCheck.map(s => rootState[s]), [
    rootState,
    statesToCheck,
  ]);

  const isPersisted = states.every(s => isPersistedState(s) && !!s._persist);
  const isRehydrated = states.every(s => isPersistedState(s) && s.rehydrated);

  useEffect(() => {
    if (isRehydrated) return;
    const timeoutId = setTimeout(() => {
      setHasRehydrationTimedOut(true);
    }, 1500);
    return () => clearTimeout(timeoutId);
  }, [isRehydrated, states]);

  return {
    rehydrated: isPersisted ? isRehydrated : false,
    timedOut: hasRehydrationTimedOut,
  };
};
